<template>
  <div class="hostel-attendance-wrapper">
    <!-- Summary Bar with Filter Tags -->
    <div v-if="eventInfo" class="mb-3">
      <div class="card border-0 bg-light">
        <div class="card-body py-2 px-3">
          <div class="summary-bar">
            <div class="summary-event-info">
              <strong>{{ eventInfo.name }}</strong>
              <span class="text-muted ml-2" v-if="timing">
                {{ timing.start_time }} – {{ timing.end_time }}
              </span>
            </div>
            <div class="summary-date">
              <span class="text-muted">Date:</span> <strong>{{ date }}</strong>
            </div>
            <div class="summary-tags">
              <a-tag
                class="filter-tag"
                :class="{ 'filter-tag--active': filterStatus === 'all' }"
                @click="$emit('filter-change', 'all')"
              >Total: {{ summary.total }}</a-tag>
              <a-tag
                color="green"
                class="filter-tag"
                :class="{ 'filter-tag--active': filterStatus === 'present' }"
                @click="$emit('filter-change', 'present')"
              >Present: {{ summary.present }}</a-tag>
              <a-tag
                color="red"
                class="filter-tag"
                :class="{ 'filter-tag--active': filterStatus === 'absent' }"
                @click="$emit('filter-change', 'absent')"
              >Absent: {{ summary.absent }}</a-tag>
              <a-tag
                color="purple"
                class="filter-tag"
                :class="{ 'filter-tag--active': filterStatus === 'leave' }"
                @click="$emit('filter-change', 'leave')"
              >Leave: {{ summary.leave || 0 }}</a-tag>
              <a-tag
                color="orange"
                class="filter-tag"
                :class="{ 'filter-tag--active': filterStatus === 'pending' }"
                @click="$emit('filter-change', 'pending')"
              >Pending: {{ summary.pending }}</a-tag>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Attendance Table -->
    <a-table
      :columns="columns"
      :dataSource="data"
      :rowKey="record => record.enrollment_id"
      :loading="loading"
      :pagination="{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ['25', '50', '100'] }"
      :scroll="{ x: 'max-content' }"
      size="middle"
      bordered
    >
      <template slot="index" slot-scope="text, record, index">
        {{ index + 1 }}
      </template>

      <template slot="student" slot-scope="text, record">
        <div>
          <strong>{{ record.student.first_name }} {{ record.student.last_name }}</strong>
          <br>
          <small class="text-muted">{{ record.student.student_code }}</small>
        </div>
      </template>

      <template slot="hostel" slot-scope="text, record">
        {{ record.hostel ? record.hostel.name : '-' }}
      </template>

      <template slot="block" slot-scope="text, record">
        {{ record.block ? record.block.name : '-' }}
      </template>

      <template slot="room" slot-scope="text, record">
        {{ record.room ? record.room.room_number : '-' }}
      </template>

      <template slot="class" slot-scope="text, record">
        <span v-if="record.class_section && record.class_section.school_class">
          {{ record.class_section.school_class.name }}
        </span>
        <span v-else>-</span>
      </template>

      <template slot="section" slot-scope="text, record">
        <span v-if="record.class_section && record.class_section.section">
          {{ record.class_section.section.name }}
        </span>
        <span v-else>-</span>
      </template>

      <template slot="status" slot-scope="text, record">
        <a-tag v-if="record.status === 0" color="green">Present</a-tag>
        <a-tag v-else-if="record.status === 1" color="red">Absent</a-tag>
        <a-tag v-else-if="record.status === 2" color="purple">Leave</a-tag>
        <a-tag v-else-if="record.status === 3" color="orange">Pending</a-tag>
        <a-tag v-else>{{ record.status }}</a-tag>
      </template>

      <template slot="punchTime" slot-scope="text, record">
        <span v-if="record.start_time">
          {{ record.start_time }}
          <span v-if="record.end_time && record.end_time !== record.start_time"> – {{ record.end_time }}</span>
        </span>
        <span v-else class="text-muted">—</span>
      </template>

      <template slot="lateDuration" slot-scope="text, record">
        <span v-if="record.late_duration" class="text-warning">{{ record.late_duration }}</span>
        <span v-else class="text-muted">—</span>
      </template>

      <template slot="earlyLeft" slot-scope="text, record">
        <span v-if="record.early_left_duration" class="text-danger">{{ record.early_left_duration }}</span>
        <span v-else class="text-muted">—</span>
      </template>
    </a-table>
  </div>
</template>

<script>
const columns = [
  { title: '#', key: 'index', scopedSlots: { customRender: 'index' }, width: 50 },
  { title: 'Student', key: 'student', scopedSlots: { customRender: 'student' }, width: 160 },
  { title: 'Hostel', key: 'hostel', scopedSlots: { customRender: 'hostel' }, width: 130 },
  { title: 'Block', key: 'block', scopedSlots: { customRender: 'block' }, width: 100 },
  { title: 'Room', key: 'room', scopedSlots: { customRender: 'room' }, width: 80 },
  { title: 'Class', key: 'class', scopedSlots: { customRender: 'class' }, width: 90 },
  { title: 'Section', key: 'section', scopedSlots: { customRender: 'section' }, width: 90 },
  { title: 'Status', key: 'status', scopedSlots: { customRender: 'status' }, width: 100 },
  { title: 'Punch Time', key: 'punchTime', scopedSlots: { customRender: 'punchTime' }, width: 160 },
  { title: 'Late', key: 'lateDuration', scopedSlots: { customRender: 'lateDuration' }, width: 100 },
  { title: 'Early Left', key: 'earlyLeft', scopedSlots: { customRender: 'earlyLeft' }, width: 110 },
]

export default {
  name: 'HostelAttendanceTable',
  props: {
    loading: { type: Boolean, default: false },
    data: { type: Array, default: () => [] },
    eventInfo: { type: Object, default: null },
    timing: { type: Object, default: null },
    date: { type: String, default: '' },
    summary: { type: Object, default: () => ({ total: 0, present: 0, absent: 0, pending: 0 }) },
    filterStatus: { type: String, default: 'all' },
    approveLoading: { type: Boolean, default: false },
  },
  data() {
    return { columns }
  },
}
</script>

<style scoped>
.summary-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.summary-event-info {
  flex: 1 1 auto;
  min-width: 0;
}

.summary-date {
  flex: 0 0 auto;
}

.summary-tags {
  flex: 0 0 auto;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.filter-tag {
  cursor: pointer;
  transition: all 0.2s;
  user-select: none;
}

.filter-tag:hover {
  opacity: 0.8;
  transform: scale(1.05);
}

.filter-tag--active {
  font-weight: bold;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.5);
}

.action-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

@media (max-width: 768px) {
  .summary-bar {
    flex-direction: column;
    align-items: flex-start;
  }

  .summary-tags {
    width: 100%;
  }

  .action-bar {
    flex-direction: column;
  }

  .action-bar .ant-btn {
    width: 100%;
  }
}
</style>
